// Centralized copy + enums for the AI Response generation experience (EAI-1497).
// Keeping user-facing strings here makes the terminology rename auditable and
// prevents the "Suggest Message" / "Customize AI Suggestion" copy from drifting
// back across the three composer surfaces (SMS / reviews / email).

export const AI_RESPONSE_COPY = {
  GENERATE: "Generate Response",
  CUSTOMIZE_TITLE: "Customize AI response",
  GENERATE_TOOLTIP: "Draft with AI",
  CUSTOMIZE_TOOLTIP: "Customize AI message",
  // EAI-1006 — the composer's one-click Refine, which reworks whatever is
  // already in the textarea rather than drafting something new.
  REFINE: "Refine",
  UNDO: "Undo",
  UNDO_TOOLTIP: "Discard the AI changes and put your original message back.",
  MAXIMIZE: "Maximize",
  MINIMIZE: "Minimize",
  MY_PREFERENCES_TITLE: "My Preferences",
  MY_PREFERENCES_BANNER:
    "Preferences apply automatically every time you use Generate Response, and pre-fill the Customize AI Response popup.",
  HIDE_UNHIDE_TITLE: "Hide/Unhide tips and tones",
  HIDE_UNHIDE_LINK: "Hide/unhide",
  SAVE: "Save",
  BACK: "Back",
  MANAGE_CUSTOM_TITLE: "Manage my custom tips & tones",
  ALWAYS_INCLUDE: "Always include",
  DEFAULT_TONE: "Default tone",
  SAVE_PREFERENCES: "Save Preferences",
  ADD_TIP: "Add tip",
  ADD_TONE: "Add tone",
  QUICK_TIPS: "Tips",
  TONES: "Tones",
  ADDITIONAL_NOTES: "Instructions for AI",
  ADDITIONAL_NOTES_OPTIONAL: "Instructions for AI (optional)",
  ADDITIONAL_NOTES_MARK: "(optional)",
  ADDITIONAL_NOTES_TOOLTIP:
    "Tell AI anything specific you want in this message. An offer, a deadline, a detail, or a tone shift. AI blends it into the reply along with the tips and tone you picked.",
  ADDITIONAL_NOTES_PLACEHOLDER:
    "e.g. Ask if they're free to come in this Saturday for a test drive",
  REPLY_AS: "Reply as",
  REPLY_AS_SELF: "Myself",
  NO_TIPS: "No Quick Tips found",
  NO_TONES: "No Tones found",
  // "Save these preferences" checkbox (EAI-1497 final design) — persists the
  // current selection as the user's default (preserved) for this location.
  SAVE_PREFERENCES_FUTURE: "Save these preferences for future responses",
  SAVE_PREFERENCES_TOOLTIP:
    "Applies these tips and tone to every new message across all leads until you change them. Your instructions for AI aren't saved.",
  SAVED_TOAST: "Saved as your default for future responses.",
  // Add/Edit custom tip/tone form (CustomItemForm).
  CUSTOM_WHERE_USED: "Where should this {noun} be used?",
  CHANNEL_TEXT: "Text",
  CHANNEL_TEXT_HELP: "While drafting the text response.",
  CHANNEL_EMAIL: "Email",
  CHANNEL_EMAIL_HELP: "While drafting the email response.",
  CUSTOM_INSTRUCTIONS_HELP:
    "The instructions AI follows when applying this quick {noun}. Describe what you want AI to do so it can draft responses that match your intent.",
  // Per-item actions menu (own custom tips/tones only).
  MENU_EDIT: "Edit",
  MENU_DELETE: "Delete",
  CANCEL: "Cancel",
  DELETE_CONFIRM_TITLE: "Delete this {noun}?",
  // {Noun} = capitalized singular (Tip/Tone), {noun}s = lowercase plural.
  DELETE_CONFIRM_BODY:
    "This {noun} will be permanently removed from your {noun}s list. This action can’t be undone.",
};

// EAI-1006 — which composer an AI draft belongs to. Channels are tracked apart
// in aiData so generating an email never disturbs a text draft. Adding a
// channel means adding a case here, a slot in aiSlice, and an adapter in
// draftAdapters.js — nothing in the refine mechanism itself.
export const AI_CHANNEL = Object.freeze({
  TEXT: "text",
  EMAIL: "email",
});

// Refine's tooltips name what is being refined, so the email composer doesn't
// offer to refine a "message". Whole strings per channel rather than a noun
// slotted into a template: the articles differ ("a message" / "an email"), and
// a translator needs the sentence, not its pieces.
export const REFINE_TOOLTIP_BY_CHANNEL = Object.freeze({
  [AI_CHANNEL.TEXT]: "Refine message",
  [AI_CHANNEL.EMAIL]: "Refine email",
});

export const REFINE_DISABLED_TOOLTIP_BY_CHANNEL = Object.freeze({
  [AI_CHANNEL.TEXT]: "Write or generate a message first, then refine it.",
  [AI_CHANNEL.EMAIL]: "Write or generate an email first, then refine it.",
});

// `mode` on AiSuggestionUserInput, sent alongside `existingMessage`. REFINE
// reworks the supplied draft; REWRITE would ignore it and draft fresh. Only
// REFINE is sent today — every other call omits the draft entirely and lets the
// backend apply its own default. REWRITE is here because it is part of the API
// contract, and is what a "rewrite this" control would send.
export const AI_REQUEST_MODE = Object.freeze({
  REFINE: "REFINE",
  REWRITE: "REWRITE",
});

// EAI-1006 — what a draft call is trying to do, from the UI's point of view.
// Doubles as the "which control is spinning" discriminator while a call is in
// flight, so keep these values stable rather than inlining the strings. Maps
// onto AI_REQUEST_MODE on the way out to the API.
export const AI_ACTION = Object.freeze({
  // Draft from the selected tips/tone/instructions alone, ignoring the composer.
  GENERATE: "generate",
  // Rework the text already in the composer.
  REFINE: "refine",
});

// Channel values used by the CustomItemForm picker; they match the admin
// COMMUNICATION_CHANNEL_OPTIONS and resolve to use case ids on create.
export const COMMUNICATION_CHANNEL = {
  TEXT: "Text",
  EMAIL: "Email",
};

// suggestionType discriminator shared with the admin config
// (mirrors ADDING_TYPE in adminDashboard/settings/CustomizeAiSuggestions).
export const SUGGESTION_TYPE = {
  TIPS: "QUICK_TIPS",
  TONES: "QUICK_TONES",
  ADDITIONAL_NOTES: "ADDITIONAL_NOTES",
};
